import React from 'react';
import { Card } from 'primereact/card';

export const OcularVisitStep = ({ step }) => {
    // If step is pending (not done by admin yet) or in-progress, show status.
    // If step is completed, show the data.

    // Check if we have data
    const visitData = step?.metadata?.visit_data;
    const status = step?.status || 'pending';

    // Formatting helper
    const formatDate = (dateString) => {
        if (!dateString) return 'Por definir';
        return new Date(dateString).toLocaleString('es-MX', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const renderStatusMessage = () => {
        switch (status) {
            case 'pending':
                return (
                    <div className="bg-yellow-50 p-3 border-round border-left-3 border-yellow-500 text-left">
                        <span className="font-bold text-yellow-800 block mb-1">En espera de programación</span>
                        <span className="text-sm text-yellow-700">Un administrador se pondrá en contacto pronto para programar tu visita ocular.</span>
                    </div>
                );
            case 'scheduled':
                return (
                    <div className="bg-blue-50 p-3 border-round border-left-3 border-blue-500 text-left">
                        <span className="font-bold text-blue-800 block mb-1">Visita Programada</span>
                        <span className="text-sm text-blue-700">Tu visita ocular ha sido programada. Por favor confirma la fecha tentativa abajo.</span>
                    </div>
                );
            case 'postponed':
                return (
                    <div className="bg-orange-50 p-3 border-round border-left-3 border-orange-500 text-left">
                        <span className="font-bold text-orange-800 block mb-1">Visita Reprogramada</span>
                        <span className="text-sm text-orange-700">Tu visita ha sido reprogramada. Revisa la nueva fecha asignada.</span>
                    </div>
                );
            case 'paused':
                return (
                    <div className="bg-gray-100 p-3 border-round border-left-3 border-gray-500 text-left">
                        <span className="font-bold text-gray-800 block mb-1">Proceso Pausado</span>
                        <span className="text-sm text-gray-700">Tu visita ocular se encuentra pausada. Nos pondremos en contacto contigo.</span>
                    </div>
                );
            case 'completed':
                // Usually handled by showing details, but if we need a banner:
                return (
                    <div className="mt-4 pt-3 border-top-1 border-gray-300 text-center">
                        <span className="text-green-600 font-bold flex align-items-center justify-content-center gap-2">
                            <i className="pi pi-check-circle"></i> Visita Completada y Verificada
                        </span>
                    </div>
                );
            case 'unsuccessful':
                return (
                    <div className="bg-red-50 p-3 border-round border-left-3 border-red-500 text-left">
                        <span className="font-bold text-red-800 block mb-1">
                            <i className="pi pi-times-circle mr-2"></i>Visita No Aprobada
                        </span>
                        <span className="text-sm text-red-700">
                            Tu visita ocular no fue aprobada. El proceso no puede avanzar por el momento. 
                            Un administrador se comunicar&aacute; contigo si hay novedades o si es posible reprogramar el proceso.
                        </span>
                    </div>
                );
            default:
                return null;
        }
    };

    const getVisitsToDisplay = () => {
        if (!visitData) return [];

        // Identify keys that are NOT visits
        const excludedKeys = ['email_sent_to', 'cc_emails', 'admin_comment', 'email_content', 'email_subject'];

        const visits = [];

        // Check for legacy flat structure (has 'full_name' directly)
        if (visitData.full_name) {
            visits.push({
                type: 'Visita Particular',
                data: {
                    fullName: visitData.full_name,
                    address: visitData.address,
                    postalCode: visitData.postal_code,
                    municipality: visitData.municipality,
                    colonia: visitData.colonia,
                    visitsCount: visitData.visits_count,
                    tentativeDate: visitData.tentative_date
                }
            });
        } else {
            // New structure - iterate keys
            Object.keys(visitData).forEach(key => {
                if (!excludedKeys.includes(key)) {
                    visits.push({
                        type: key,
                        data: visitData[key]
                    });
                }
            });
        }
        return visits;
    };

    const visitsList = getVisitsToDisplay();

    return (
        <div className="flex flex-column gap-3">
            <div className="p-4 text-center">
                <i className="pi pi-eye text-6xl text-blue-500 mb-3"></i>
                <h3 className="text-xl font-bold mb-2">Visitas Oculares Agendadas</h3>

                <div className="max-w-30rem mx-auto mb-4">
                    <p className="text-gray-600 mb-4">
                        Gestionamos una visita ocular para verificar tus datos antes de formalizar la cotización.
                    </p>
                    {renderStatusMessage()}
                </div>

                {visitsList.length > 0 && status !== 'pending' && (
                    <div className="flex flex-column gap-4 align-items-center w-full">
                        {visitsList.map((visit, index) => (
                            <div key={index} className="text-left surface-ground p-4 border-round-xl inline-block shadow-2" style={{ maxWidth: '600px', width: '100%' }}>
                                <div className="mb-3 border-bottom-1 border-gray-300 pb-2">
                                    <h4 className="m-0 text-blue-800">{visit.type}</h4>
                                </div>

                                {visit.data.tentativeDate && (
                                    <div className="mb-4 text-center pb-3 border-bottom-1 border-gray-200">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Fecha Tentativa</span>
                                        <span className="text-2xl font-bold text-indigo-700">
                                            {status !== 'paused' && status !== 'completed' && status !== 'pending' ? formatDate(visit.data.tentativeDate) :
                                                (status === 'completed' ? formatDate(visit.data.tentativeDate) : "Por definir")}
                                        </span>
                                    </div>
                                )}

                                <div className="grid">
                                    <div className="col-12 mb-3">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Nombre de contacto</span>
                                        <span className="text-xl font-medium text-900">{visit.data.fullName || 'N/A'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 mb-3">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Recibe</span>
                                        <span className="text-xl font-medium text-900">{visit.data.recipientName || 'N/A'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 mb-3">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Teléfono</span>
                                        <span className="text-xl font-medium text-900">{visit.data.phoneNumber || 'N/A'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 mb-3">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Código Postal</span>
                                        <span className="text-xl font-medium text-900">{visit.data.postalCode || 'N/A'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 mb-3">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Delegación / Municipio</span>
                                        <span className="text-xl font-medium text-900">{visit.data.municipality || 'N/A'}</span>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Colonia</span>
                                        <span className="text-xl font-medium text-900">{visit.data.colonia || 'N/A'}</span>
                                    </div>
                                    <div className="col-12">
                                        <span className="block text-gray-500 text-sm font-bold uppercase mb-1">Dirección</span>
                                        <span className="text-lg text-700">{visit.data.address || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
